// Membership Types for Enhanced Subscription System

// Import and re-export SubscriptionTier for backwards compatibility with components that import from this file
import { SubscriptionTier } from './subscription';
export { SubscriptionTier };

export enum MembershipType {
  Individual = 'Individual',
  Family = 'Family'
}

export enum MembershipStatus {
  Active = 'Active',
  Cancelled = 'Cancelled',
  PastDue = 'PastDue',
  Incomplete = 'Incomplete',
  IncompleteExpired = 'IncompleteExpired',
  Trialing = 'Trialing',
  Unpaid = 'Unpaid',
  Suspended = 'Suspended'
}

export enum FamilyMemberStatus {
  Active = 'Active',
  Pending = 'Pending',
  Inactive = 'Inactive',
  Removed = 'Removed'
}

export enum ChurnRiskLevel {
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
  Critical = 'Critical'
}

export enum TransitionType {
  Upgrade = 'Upgrade',
  Downgrade = 'Downgrade',
  Change = 'Change'
}

export interface MembershipTier {
  id?: number;
  tier: SubscriptionTier;
  name: string;
  description: string;
  monthlyPrice: number;
  cutsPerMonth: number | null; // null = unlimited
  discountPercentage?: number;
  canRollover?: boolean;
  maxRolloverCuts?: number;
  maxFamilyMembers?: number;
  priorityBooking?: boolean;
  cancelAnytime?: boolean;
  specialPerks?: string[];
  isPopular?: boolean;
  displayOrder?: number;
  isActive?: boolean;
  stripeProductId?: string;
  stripePriceId?: string;
  // API compatibility fields (backend may return different property names)
  serviceDiscountPercentage?: number;
  productDiscountPercentage?: number;
  benefits?: string[];
  isUnlimited?: boolean;
  familyMemberSlots?: number;
  isFamilyPlan?: boolean;
  estimatedMonthlySavings?: number;
}

export interface EnhancedSubscription {
  id: number;
  userId: string;
  userName: string;
  userEmail: string;
  tier: SubscriptionTier;
  membershipType: MembershipType;
  status: MembershipStatus;
  monthlyPrice: number;
  cutsPerMonth: number | null; // null = unlimited
  discountPercentage: number;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  nextBillingDate: string;
  cancelledAt?: string;
  cancellationReason?: string;
  cancelAtPeriodEnd: boolean;
  trialEndsAt?: string;
  createdAt: string;
  updatedAt: string;

  // Usage tracking
  usedCuts: number;
  remainingCuts: number | null; // null = unlimited
  rolloverCuts: number;

  // Family plan specific
  familyMembers: FamilyMember[];
  maxFamilyMembers: number;

  // Payment
  stripeSubscriptionId?: string;
  stripeCustomerId?: string;
  paymentMethodLast4?: string;
  paymentMethodBrand?: string;

  // Churn prediction
  churnRisk?: MembershipChurnRisk;
  engagementScore?: number;
  daysSinceLastBooking?: number;
}

export interface FamilyMember {
  id: number;
  subscriptionId: number;
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  relationshipType?: string;
  status: FamilyMemberStatus;
  invitationSentAt?: string;
  invitationAcceptedAt?: string;
  addedAt: string;
  removedAt?: string;

  // Usage tracking
  usedCuts: number;
  lastBookingDate?: string;
}

export interface MembershipUsage {
  subscriptionId: number;
  userId: string;
  tier: SubscriptionTier;
  period: string; // YYYY-MM format
  cutsAllowed: number | null; // null = unlimited
  cutsUsed: number;
  cutsRemaining: number | null; // null = unlimited
  rolloverCuts: number;
  discountAmount: number;
  savingsTotal: number;

  // Family breakdown
  familyMemberUsage?: FamilyMemberUsage[];

  // History
  usageHistory: UsageHistoryEntry[];

  // Projections
  projectedUsage?: number;
  utilizationRate?: number; // percentage
}

export interface FamilyMemberUsage {
  familyMemberId: number;
  memberName: string;
  cutsUsed: number;
  lastBookingDate?: string;
  percentage: number;
}

export interface UsageHistoryEntry {
  id: number;
  bookingId: number;
  bookingDate: string;
  serviceName: string;
  barberName: string;
  barbershopName: string;
  originalPrice: number;
  discountApplied: number;
  finalPrice: number;
  cutUsed: boolean;
  familyMemberName?: string;
}

export interface TierEligibility {
  userId: string;
  currentTier?: SubscriptionTier;
  eligibleTiers: SubscriptionTier[];
  canUpgrade: boolean;
  canDowngrade: boolean;
  upgradeOptions: TierTransitionOption[];
  downgradeOptions: TierTransitionOption[];
  restrictions?: string[];
}

export interface TierTransitionOption {
  targetTier: SubscriptionTier;
  transitionType: TransitionType;
  priceDifference: number;
  prorationAmount: number;
  effectiveDate: string;
  immediatePayment: boolean;
  benefits: string[];
  drawbacks: string[];
  retentionOffer?: RetentionOffer;
}

export interface RetentionOffer {
  id: number;
  offerType: string;
  description: string;
  discountPercentage?: number;
  discountAmount?: number;
  freeMonths?: number;
  bonusCuts?: number;
  expiresAt: string;
  conditions?: string[];
}

export interface MembershipChurnRisk {
  userId: string;
  subscriptionId: number;
  riskLevel: ChurnRiskLevel;
  riskScore: number; // 0-100
  riskFactors: string[];
  engagementMetrics: MembershipEngagementMetrics;
  predictedChurnDate?: string;
  recommendedActions: string[];
  lastCalculatedAt: string;
}

export interface MembershipEngagementMetrics {
  daysSinceLastBooking: number;
  bookingsLast30Days: number;
  bookingsLast90Days: number;
  averageBookingFrequency: number; // days
  utilizationRate: number; // percentage
  loginFrequency: number; // logins per month
  lastLoginDate?: string;
  appOpenCount: number;
  familyEngagement?: number; // for family plans
}

// API DTOs

export interface SubscribeToMembershipDto {
  tier: SubscriptionTier;
  membershipType: MembershipType;
  paymentMethodId: string;
  familyMembers?: CreateFamilyMemberDto[];
  promoCode?: string;
  billingAddress?: BillingAddress;
}

export interface CreateFamilyMemberDto {
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  relationshipType?: string;
  sendInvitation?: boolean;
}

export interface UpdateMembershipDto {
  paymentMethodId?: string;
  billingAddress?: BillingAddress;
}

export interface UpgradeMembershipDto {
  newTier: SubscriptionTier;
  paymentMethodId?: string;
  immediateUpgrade: boolean;
}

export interface DowngradeMembershipDto {
  newTier: SubscriptionTier;
  reason?: string;
  scheduleEndOfPeriod: boolean;
}

export interface CancelMembershipDto {
  reason: string;
  feedback?: string;
  immediateCancel: boolean;
}

export interface AddFamilyMemberDto {
  subscriptionId: number;
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  relationshipType?: string;
  sendInvitation?: boolean;
}

export interface RemoveFamilyMemberDto {
  familyMemberId: number;
  reason?: string;
}

export interface BillingAddress {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface MembershipAnalytics {
  totalRevenue: number;
  monthlyRecurringRevenue: number;
  activeSubscriptions: number;
  newSubscriptions: number;
  cancelledSubscriptions: number;
  churnRate: number;

  tierDistribution: TierDistribution[];
  revenueByTier: RevenueByTier[];
  subscriptionTrends: SubscriptionTrend[];
  churnAnalysis: MembershipChurnAnalysis;
  familyPlanMetrics: FamilyPlanMetrics;
  retentionMetrics: RetentionMetrics;

  averageRevenuePerUser: number;
  lifetimeValue: number;
  customerAcquisitionCost?: number;
}

export interface TierDistribution {
  tier: SubscriptionTier;
  count: number;
  percentage: number;
  revenue: number;
  averageUsage: number;
}

export interface RevenueByTier {
  tier: SubscriptionTier;
  monthlyRevenue: number;
  totalRevenue: number;
  subscriptionCount: number;
  averageRevenuePerUser: number;
  growthRate: number;
}

export interface SubscriptionTrend {
  date: string;
  newSubscriptions: number;
  cancelledSubscriptions: number;
  upgrades: number;
  downgrades: number;
  netChange: number;
  activeTotal: number;
  revenue: number;
}

export interface MembershipChurnAnalysis {
  totalChurned: number;
  churnRate: number;
  churnedByTier: Record<SubscriptionTier, number>;
  topChurnReasons: MembershipChurnReason[];
  averageDaysToChurn: number;
  churnTrends: MembershipChurnTrend[];
  predictedChurns: MembershipChurnPrediction[];
}

export interface MembershipChurnReason {
  reason: string;
  count: number;
  percentage: number;
}

export interface MembershipChurnTrend {
  month: string;
  churnedCount: number;
  churnRate: number;
  revenueImpact: number;
}

export interface MembershipChurnPrediction {
  userId: string;
  userName: string;
  email: string;
  tier: SubscriptionTier;
  riskLevel: ChurnRiskLevel;
  riskScore: number;
  predictedDate: string;
  recommendedAction: string;
}

export interface FamilyPlanMetrics {
  totalFamilyPlans: number;
  totalFamilyMembers: number;
  averageMembersPerPlan: number;
  familyPlanRevenue: number;
  adoptionRate: number;
  familyEngagementRate: number;
  mostActiveFamily?: string;
}

export interface RetentionMetrics {
  overallRetentionRate: number;
  retentionByTier: Record<SubscriptionTier, number>;
  averageLifetimeMonths: number;
  cohortAnalysis: CohortData[];
  winBackRate?: number;
}

export interface CohortData {
  cohortMonth: string;
  initialSize: number;
  month0: number;
  month1: number;
  month2: number;
  month3: number;
  month6: number;
  month12: number;
  retentionRate: number;
}

// UI Props Types

export interface MembershipTierCardProps {
  tier: MembershipTier;
  currentTier?: SubscriptionTier;
  isRecommended?: boolean;
  onSelect: (tier: SubscriptionTier) => void;
  isSelected?: boolean;
  disabled?: boolean;
}

export interface TierComparisonModalProps {
  isOpen: boolean;
  onClose: () => void;
  tiers: MembershipTier[];
  currentTier?: SubscriptionTier;
  onSelectTier: (tier: SubscriptionTier) => void;
}

export interface MembershipUsageCardProps {
  usage: MembershipUsage;
  subscription: EnhancedSubscription;
  onViewHistory?: () => void;
}

export interface FamilyMemberManagerProps {
  subscription: EnhancedSubscription;
  onAddMember: () => void;
  onRemoveMember: (memberId: number) => void;
  onRefresh: () => void;
}

export interface AddFamilyMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscriptionId: number;
  currentMemberCount: number;
  maxMembers: number;
  onSuccess: () => void;
}

export interface TierTransitionModalProps {
  isOpen: boolean;
  onClose: () => void;
  transitionType: TransitionType;
  currentSubscription: EnhancedSubscription;
  targetTier: SubscriptionTier;
  transitionOption: TierTransitionOption;
  onConfirm: () => Promise<void>;
}

export interface CancelMembershipModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscription: EnhancedSubscription;
  onConfirm: (data: CancelMembershipDto) => Promise<void>;
  retentionOffer?: RetentionOffer;
}

export interface ChurnRiskBadgeProps {
  churnRisk: MembershipChurnRisk;
  engagementMetrics: MembershipEngagementMetrics;
  showDetails?: boolean;
  className?: string;
}

export interface BenefitsListProps {
  tier: MembershipTier;
  highlights?: string[];
  showAllBenefits?: boolean;
  compactView?: boolean;
}

// Subscription Management Types
// Wire shapes mirror the backend CustomerMembershipTierController /
// CustomerMembershipPaymentController DTOs (SubscriptionDTOs.cs,
// ProrationDTOs.cs, CustomerMembershipDTOs.cs).

export interface CancellationPreview {
  canCancelImmediately: boolean;
  canCancelAtPeriodEnd: boolean;
  refundAmount: number;
  currentPeriodEnd: string;
  daysRemaining: number;
  amountPaidThisPeriod: number;
  cutsUsedThisPeriod: number;
}

export interface CancelSubscriptionRequest {
  cancellationType: 'Immediate' | 'EndOfPeriod' | 'Scheduled';
  reason?: 'TooExpensive' | 'NotUsingEnough' | 'FoundAlternative' | 'MovingAway' | 'ServiceIssues' | 'Other';
  feedback?: string;
  requestRefund: boolean;
  scheduledCancellationDate?: string;
  acceptRetentionOffer?: boolean;
  selectedRetentionOfferId?: number;
}

export interface CancellationResult {
  success: boolean;
  message: string;
  effectiveDate?: string;
  refundAmount?: number;
  serviceAccessUntil?: string;
}

export interface ProrationPreview {
  currentTier: SubscriptionTier;
  newTier: SubscriptionTier;
  currentTierPrice: number;
  newTierPrice: number;
  prorationCredit: number;
  amountDueNow: number;
  amountDueToday: number;
  effectiveDate: string;
  daysRemainingInPeriod: number;
  isUpgrade: boolean;
  summary: string;
}

/**
 * Response from POST /memberships/create-checkout-session
 * (CheckoutSessionResponseDto). Note the redirect URL field is
 * `checkoutUrl`, not `url`.
 */
export interface CheckoutSessionResponse {
  sessionId: string;
  checkoutUrl: string;
  publicKey: string;
}

// Promo Code Types

export interface CreatePromoCodeRequest {
  code: string;
  discountType: 'Percentage' | 'FixedAmount';
  discountValue: number;
  expiresAt?: string;
  maxUses?: number;
  applicableTiers?: SubscriptionTier[];
}

export interface PromoCode {
  id: number;
  code: string;
  discountType: 'Percentage' | 'FixedAmount';
  discountValue: number;
  expiresAt?: string;
  maxUses?: number;
  timesUsed: number;
  isActive: boolean;
  createdAt: string;
}

export interface PromoCodeValidation {
  isValid: boolean;
  message: string;
  discountAmount?: number;
  code?: PromoCode;
}
